import Vue from 'vue'
import store from '../store'

class VerificationManager {
    constructor() {
        this.pendingRequests = new Map()
        this.verificationInProgress = false
        this.currentVerificationPromise = null
    }

    /**
     * Handle verification requirement from interceptor
     */
    async handleVerificationRequired(error) {
        const requestId = this.generateRequestId()
        const verificationData = error.response.data.verification || {}
        const debugData = error.response.data.debug || {}

        console.log('handleVerificationRequired', verificationData)

        // Store the original request
        this.pendingRequests.set(requestId, {
            config: error.config,
            timestamp: Date.now(),
            verificationType: verificationData.type
        })

        try {
            // If verification is already in progress, wait for it
            if (this.verificationInProgress && this.currentVerificationPromise) {
                await this.currentVerificationPromise
            } else {
                // Start new verification flow
                await this.startVerification(verificationData, debugData)
            }

            // Retry the original request after successful verification
            return await this.retryRequest(requestId)
        } catch (verificationError) {
            // Clean up on failure
            this.pendingRequests.delete(requestId)
            throw verificationError
        }
    }

    /**
     * Start the verification flow
     */
    async startVerification(verificationData, debugData = {}) {
        if (this.verificationInProgress) {
            return this.currentVerificationPromise
        }

        this.verificationInProgress = true

        this.currentVerificationPromise = new Promise((resolve, reject) => {
            // New Vue instance to create the popup
            const vm = new Vue()

            vm.$popup('VerificationCodeInput', {
                verificationType: verificationData.type,
                context: verificationData.context || {},
                instructions: verificationData.instructions,
                retryAfter: verificationData.retry_after || 60,
                debugCode: debugData.verification_code || null,
                // Pass async handlers
                onVerified: (data) => {
                    console.log('onVerified called within VerificationManager', data)

                    this.handleVerificationSuccess(data, resolve)
                },
                onCancel: () => {
                    console.log('onCancel called within VerificationManager')

                    this.handleVerificationCancel(reject)
                }
            })

            // Clean up the Vue instance
            vm.$destroy()
        })

        return this.currentVerificationPromise
    }

    /**
     * Handle successful verification
     */
    handleVerificationSuccess(data, resolve) {
        this.verificationInProgress = false

        // Store verification token in store
        store.commit('user/setVerificationStatus', {
            verified: true,
            token: data.token,
            type: data.type
        })

        resolve(data)
    }

    /**
     * Handle verification cancellation
     */
    handleVerificationCancel(reject) {
        this.verificationInProgress = false

        reject(new Error('Verification cancelled by user'))
    }

    /**
     * Retry the original request
     */
    async retryRequest(requestId) {
        const pendingRequest = this.pendingRequests.get(requestId)

        if (!pendingRequest) {
            throw new Error('Request not found')
        }

        // Add verification token to headers
        const verificationToken = store.state.user.verificationToken
        if (verificationToken) {
            pendingRequest.config.headers = {
                ...pendingRequest.config.headers,
                'X-Verification-Token': verificationToken,
            }
        }

        // Clean up
        this.pendingRequests.delete(requestId)

        // Retry the request
        return await window.axios.request(pendingRequest.config)
    }

    /**
     * Generate unique request ID
     */
    generateRequestId() {
        return crypto.randomUUID();
    }
}

export default new VerificationManager()
